import { useEffect, useRef, useState } from 'react'
import { api } from '../lib/api'

declare global {
  interface Window {
    google?: any
  }
}

const GIS_SRC = 'https://accounts.google.com/gsi/client'

/** 只載入一次 Google Identity Services */
function loadGis(): Promise<void> {
  if (window.google?.accounts?.id) return Promise.resolve()
  const existing = document.querySelector(`script[src="${GIS_SRC}"]`)
  if (existing) {
    return new Promise((resolve, reject) => {
      existing.addEventListener('load', () => resolve())
      existing.addEventListener('error', () => reject(new Error('無法載入 Google 登入元件')))
    })
  }
  return new Promise((resolve, reject) => {
    const s = document.createElement('script')
    s.src = GIS_SRC
    s.async = true
    s.defer = true
    s.onload = () => resolve()
    s.onerror = () => reject(new Error('無法載入 Google 登入元件（請確認網路可連到 accounts.google.com）'))
    document.head.appendChild(s)
  })
}

export default function Login({ onSignedIn }: { onSignedIn: () => void }) {
  const btnRef = useRef<HTMLDivElement>(null)
  const [error, setError] = useState('')
  const [status, setStatus] = useState('準備登入…')
  const [notConfigured, setNotConfigured] = useState(false)

  useEffect(() => {
    let cancelled = false

    ;(async () => {
      try {
        const cfg = await api.get<{ client_id: string | null; configured: boolean }>('/auth/config')
        if (cancelled) return

        if (!cfg.client_id) {
          setNotConfigured(true)
          setStatus('')
          return
        }

        await loadGis()
        if (cancelled) return

        window.google.accounts.id.initialize({
          client_id: cfg.client_id,
          callback: async (resp: { credential?: string }) => {
            if (!resp.credential) {
              setError('Google 沒有回傳憑證，請再試一次')
              return
            }
            setStatus('驗證中…')
            setError('')
            try {
              await api.post('/auth/session', { credential: resp.credential })
              onSignedIn()
            } catch (e: any) {
              setStatus('')
              setError(e.message || String(e))
            }
          },
        })

        window.google.accounts.id.renderButton(btnRef.current, {
          theme: 'outline',
          size: 'large',
          text: 'signin_with',
          locale: 'zh_TW',
          width: 280,
        })
        setStatus('')
      } catch (e: any) {
        if (!cancelled) {
          setStatus('')
          setError(e.message || String(e))
        }
      }
    })()

    return () => { cancelled = true }
  }, [onSignedIn])

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center',
      justifyContent: 'center', background: 'var(--bg)', padding: 20,
    }}>
      <div className="panel" style={{ width: 420, maxWidth: '100%', marginBottom: 0 }}>
        <div style={{ textAlign: 'center', marginBottom: 20 }}>
          <h1 style={{ fontSize: 20, margin: '0 0 4px' }}>文案外包管理系統</h1>
          <p style={{ fontSize: 12, color: 'var(--ink-soft)', margin: 0 }}>崴浤科技</p>
        </div>

        {notConfigured ? (
          <div className="alert warn" style={{ marginBottom: 0 }}>
            <b>尚未設定 Google 登入</b>
            <br />
            伺服器還沒有 <code>GOOGLE_OAUTH_CLIENT_ID</code>。
            請先在 Google Cloud Console 建立 OAuth 用戶端 ID，再設定到 Cloudflare。
          </div>
        ) : (
          <>
            {error && <div className="alert error">{error}</div>}
            <div ref={btnRef} style={{ display: 'flex', justifyContent: 'center' }} />
            {status && (
              <p style={{ textAlign: 'center', fontSize: 13, color: 'var(--ink-soft)', marginBottom: 0 }}>
                {status}
              </p>
            )}
            <p style={{
              fontSize: 12, color: 'var(--ink-soft)', textAlign: 'center',
              marginTop: 18, marginBottom: 0, lineHeight: 1.7,
            }}>
              請使用公司核可的 Google 帳號登入。
              <br />
              帳號需事先加入允許名單才能進入系統。
            </p>
          </>
        )}
      </div>
    </div>
  )
}
